import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import type { ConversionEventData } from '@/events/conversion';

import type { ConversionEventType } from '../../contracts';
import { BaseAutoTracker } from '../../auto/BaseAutoTracker';
import type { TrackerFactory } from '../../tracks/TrackerFactory';
import { buildSelector, resolveDataTrackEvent } from './selector-utils';

export class SelectorDetector extends BaseAutoTracker {
  readonly name = 'SelectorDetector';
  private readonly factory: TrackerFactory;

  private readonly selectors: string[];

  constructor(
    context: PixelRuntimeContext,
    factory: TrackerFactory,
    selectors: string[] = []
  ) {
    super(context);
    this.factory = factory;
    this.selectors =
      selectors.length > 0 ? selectors : ['[data-track]', '[data-track-event]'];
  }

  protected onInit(): void {
    if (!this.isDocumentAvailable()) return;
    this.addDocumentListener('click', event => this.handleClick(event), true);
  }

  protected onDestroy(): void {}

  private handleClick(event: Event): void {
    let element = event.target instanceof Element ? event.target : null;

    while (element && element !== document.body) {
      const match = this.matchElement(element);
      if (match) {
        this.factory.trackAuto(match.type, match.data, {
          origin: 'auto:selector',
        });
        return;
      }
      element = element.parentElement;
    }
  }

  private matchElement(
    element: Element
  ): { type: ConversionEventType; data: ConversionEventData } | null {
    for (const selector of this.selectors) {
      const built = buildSelector(selector);
      if (!element.matches(built)) continue;

      const trackValue =
        element.getAttribute('data-track') ??
        element.getAttribute('data-track-event') ??
        element.getAttribute('data-event');

      if (!trackValue) continue;

      const type = resolveDataTrackEvent(trackValue);
      if (!type) continue;

      const data: ConversionEventData = {};
      const valueAttr = element.getAttribute('data-track-value');
      if (valueAttr && type === 'PURCHASE') {
        Object.assign(data, { value: Number(valueAttr) || undefined });
      }

      return { type, data };
    }

    const dataTrack = element.getAttribute('data-track');
    if (dataTrack) {
      const type = resolveDataTrackEvent(dataTrack);
      if (type) return { type, data: {} };
    }

    return null;
  }
}
